import railsPostFetch from '@aytm/lib/common/rails-post-fetch'

type NextInterval = number | null
type ContinuePolling = boolean

export type ConnectionStatus = 'ok' | 'unstable' | 'error'

const UNSTABLE_THRESHOLD = 2
const ERROR_THRESHOLD = 5

export type PollingService<DataType = unknown, RequestBodyType = unknown> = {
  set url(url: string | (() => string) | null)
  get url(): string | (() => string) | null
  set method(method: string)
  get method(): string
  set interval(interval: number)
  get interval(): number
  set fetchBody(fetcher: (() => Promise<RequestBodyType>) | null)
  set onResponse(callback: ((response: Response) => ContinuePolling) | null)
  set onData(callback: ((data: DataType) => NextInterval) | null)
  set onError(callback: (error: Error) => void)
  set onConnectionStatusChange(callback: (status: ConnectionStatus) => void)
  stop(): void
  get pollingActive(): boolean
}

const defaultOnError = (error: Error) => {
  // eslint-disable-next-line no-console
  console.error('[GlobalChat] PollingService: error', error)
}

const FAIL_TRIES_LIMIT = 100

export default function PollingService<DataType = unknown, RequestBodyType = unknown>(): PollingService<
  DataType,
  RequestBodyType
> {
  let url: string | (() => string) | null = null
  let method: string = 'POST'
  let interval: number = 0
  let pollTimeout: ReturnType<typeof setTimeout> | null = null
  let fetchBody: () => Promise<RequestBodyType> | null = null
  let onData: (data: DataType) => NextInterval | null = null
  let onResponse: ((response: Response) => ContinuePolling) | null = null
  let onError: (error: Error) => void = defaultOnError
  let onConnectionStatusChange: ((status: ConnectionStatus) => void) | null = null
  let failTriesCount = FAIL_TRIES_LIMIT
  let consecutiveFailures = 0
  let lastConnectionStatus: ConnectionStatus = 'ok'

  function emitConnectionStatus() {
    const status: ConnectionStatus =
      consecutiveFailures >= ERROR_THRESHOLD ? 'error' : consecutiveFailures >= UNSTABLE_THRESHOLD ? 'unstable' : 'ok'
    if (status !== lastConnectionStatus) {
      lastConnectionStatus = status
      onConnectionStatusChange?.(status)
    }
  }

  function stopPoll() {
    if (null != pollTimeout) {
      clearTimeout(pollTimeout)
      pollTimeout = null
    }
  }

  async function poll() {
    if (null === url) {
      throw new Error('URL is not set')
    }
    const body = 'GET' !== method && fetchBody ? await fetchBody() : null
    const response = await railsPostFetch('function' === typeof url ? url() : url, {
      method,
      body: body ? JSON.stringify(body) : void 0
    })
    const goodResponse = response.status === 200 || response.status === 304
    if (onData && (onResponse ? onResponse(response) : goodResponse)) {
      const data = (await response.json()) as DataType
      const nextInterval = onData(data)
      if (null != nextInterval) {
        interval = nextInterval
      }
      failTriesCount = FAIL_TRIES_LIMIT
      consecutiveFailures = 0
      emitConnectionStatus()
      schedulePoll()
    } else {
      throw new Error(!onData ? 'onData callback is not set' : `bad response ${response.status}`)
    }
  }

  function schedulePoll() {
    stopPoll()
    if (0 < interval) {
      pollTimeout = setTimeout(() => {
        void poll().catch((error: Error) => {
          onError(error)
          consecutiveFailures += 1
          emitConnectionStatus()
          failTriesCount -= 1
          if (failTriesCount <= 0) {
            failTriesCount = FAIL_TRIES_LIMIT
            // set interval to 30 seconds on failed after too many tries
            interval = 30000
            schedulePoll()
            onError(new Error(`[GlobalChat] PollingService: poll failed after too many tries`))
          } else {
            schedulePoll()
          }
        })
      }, interval)
    }
  }

  const service: PollingService<DataType, RequestBodyType> = {
    set url(newUrl: string | (() => string) | null) {
      url = newUrl
    },
    get url() {
      return url
    },
    set method(newMethod: string) {
      method = newMethod
    },
    get method() {
      return method
    },
    set interval(newInterval: number) {
      interval = newInterval
      schedulePoll()
    },
    get interval() {
      return interval
    },
    set fetchBody(fetcher: (() => Promise<RequestBodyType>) | null) {
      fetchBody = fetcher
    },
    set onResponse(callback: ((response: Response) => ContinuePolling) | null) {
      onResponse = callback
    },
    set onData(callback: ((data: DataType) => NextInterval) | null) {
      onData = callback
    },
    set onError(callback: (error: Error) => void) {
      onError = (error: Error) => {
        try {
          callback(error)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[GlobalChat] PollingService: error in onError callback', error)
        }
      }
    },
    set onConnectionStatusChange(callback: (status: ConnectionStatus) => void) {
      onConnectionStatusChange = callback
    },
    stop() {
      failTriesCount = FAIL_TRIES_LIMIT
      consecutiveFailures = 0
      lastConnectionStatus = 'ok'
      service.interval = 0
    },
    get pollingActive() {
      return 0 < service.interval && null != pollTimeout
    }
  }

  return service
}
